import { AfterViewInit, Component, ElementRef, Inject, inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import {
    LEKSAND_COORD,
    MapToolsType,
    resolutions,
    STOCKHOLM_COORD,
    SWEREF_99_TM,
    TrvNgMapComponent,
    TrvNgMapOverlayService,
    TrvNgMapService,
    TrvMap,
    LayerListItem,
    MapTools,
    PinBlueUserStyle,
} from "@trafikverket/trv-ng-map";
import { ChangeDetectorRef } from "@angular/core";
import { Feature, MapBrowserEvent, View } from "ol";
import { faEraser, faHand, faLocation, faMagnifyingGlass, faMapPin, faRuler, faUserCircle } from "@fortawesome/free-solid-svg-icons";
import { IconProp, layer } from "@fortawesome/fontawesome-svg-core";
import { Circle, Point } from "ol/geom";
import { TrvMapExtension } from "src/app/trv-map-extension/trv-map-extension";
import { environment } from "src/environments/environment";
import { NetDbService } from "src/app/service/net-db.service";
import { TrvMapExtensionService } from "src/app/service/trv-map-extension.service";
import { ROUTES } from "@angular/router";
import { LocalStorageService, SavedView } from "../../../service/local-storage.service";
import { SWEDEN_VIEW } from "../../../shared/constants";
import { firstValueFrom, startWith, switchMap } from "rxjs";
import { NvdbNavigationService } from "src/app/service/nvdb-navigation.service";
import { TrvGlobalMessagesService, TrvModalService } from "@trv-hub/trv-ng-common";

import _njdbPaWebbLayers from "../../../../assets/data/layer-data/njdb-layers.json";
import _jnbLayers from "../../../../assets/data/layer-data/jnb-layers.json";
import _smallLayers from "../../../../assets/data/layer-data/nvdb-layers-downscaled.json";
import { ApmService } from "@app/service/apm.service";
import { LanguageService } from "@app/service/language.service";
import { TileWMS, ImageWMS } from "ol/source";

@Component({
    selector: "app-map",
    templateUrl: "./map.component.html",
    styleUrls: ["./map.component.scss"],
    imports: [TrvNgMapComponent]
})
export class MapComponent implements AfterViewInit, OnDestroy {
    @ViewChild(TrvNgMapComponent) trvNgMap!: TrvNgMapComponent;

    @Input() east: number | null = null;
    @Input() north: number | null = null;
    @Input() scale: number | null = null;
    @Input() wmsLayerIds: string[] = [];
    @Input() backgroundLayerId: string | undefined = undefined;

    public loading: boolean = true;
    public MapTools = MapTools;
    public nvdbLayerData: any;

    public mapHeight!: string;
    public applicationName = "";
    private blurListener?: () => void;

    constructor(
        private cdref: ChangeDetectorRef,
        @Inject(TrvNgMapService) private trvMapService: TrvNgMapService,
        @Inject(TrvNgMapOverlayService)
        private trvMapOverlayService: TrvNgMapOverlayService,
        @Inject(NvdbNavigationService)
        public nvdbNavigation: NvdbNavigationService,
        @Inject(LocalStorageService)
        private localStorageService: LocalStorageService,
        @Inject(NetDbService) private netDbService: NetDbService,
        @Inject(TrvMapExtensionService)
        private trvMapExtensionService: TrvMapExtensionService,
        @Inject(TrvGlobalMessagesService)
        private globalMessagesService: TrvGlobalMessagesService,
        @Inject(TrvModalService) public trvModalService: TrvModalService,
        private renderer: Renderer2,
        @Inject(ApmService) public apmService: ApmService,
        public lang: LanguageService
    ) {
        if (environment.application == "NjdbPåWebb") {
            this.applicationName = "NJDB på webb";
        } else if (environment.application == "JNBKarta") {
            this.applicationName = "JNB Karta";
        }

        this.mapHeight = `calc(100svh - 50px - 53px)`;
        if (this.nvdbNavigation.isMobileDevice) this.mapHeight = `calc(100svh)`;

    }

    async ngOnInit() {
        if (environment.application == "NjdbPåWebb") {
            this.nvdbLayerData = _njdbPaWebbLayers;
        } else if (environment.application == "JNBKarta") {
            this.nvdbLayerData = _jnbLayers;
        } else {
            this.nvdbLayerData = _smallLayers;
        }

        //Alter nvdbdata with baseApiUrl;
        this.nvdbLayerData.Layers.LayerList.forEach((layer: any) => {
            if (layer.hasOwnProperty("Url")) {
                let url: string = layer.Url;
                layer.Url = url.replace("{api}", environment.baseApiUrl);
            }

            //Om local, fixa extern BG NORD URL
            const isLocalEnv = window.location.hostname === "localhost";
            if (isLocalEnv) {
                let layerId: string = layer.Id!;
                if (layerId == "vagnummer_bgnord") {
                    const baseBgNordCap =
                        "https://bas.gis.trafikverket.local/arcgis/rest/services/Basemap/VagnummerFarg/MapServer/WMTS/1.0.0/WMTSCapabilities.xml";
                    const baseBgNordTile =
                        "https://bas.gis.trafikverket.local/arcgis/rest/services/Basemap/VagnummerFarg/MapServer/WMTS/tile/1.0.0/Basemap_BakgrundskartaNorden/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png";
                    layer.Url = baseBgNordCap;
                    if (layer.hasOwnProperty("OverrideWmtsUrl")) {
                        layer.OverrideWmtsUrl = baseBgNordTile;
                    }
                }
            }
        });

        this.nvdbLayerData.BackgroundLayer.LayerList.forEach((layer: any) => {
            if (layer.hasOwnProperty("Url")) {
                let url: string = layer.Url;
                layer.Url = url.replace("{api}", environment.baseApiUrl);
            }

            //Om local, fixa extern BG NORD URL
            const isLocalEnv = window.location.hostname === "localhost";
            if (isLocalEnv) {
                let layerId: string = layer.Id!;

                if (layerId == "5BakgrundskartaNorden") {
                    const baseBgNordCap =
                        "https://bas.gis.trafikverket.local/arcgis/rest/services/Basemap/BakgrundskartaNorden/MapServer/WMTS/1.0.0/WMTSCapabilities.xml";
                    const baseBgNordTile =
                        "https://bas.gis.trafikverket.local/arcgis/rest/services/Basemap/BakgrundskartaNorden/MapServer/WMTS/tile/1.0.0/Basemap_BakgrundskartaNorden/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png";
                    layer.Url = baseBgNordCap;
                    if (layer.hasOwnProperty("OverrideWmtsUrl")) {
                        layer.OverrideWmtsUrl = baseBgNordTile;
                    }
                }
            }
        });

        if (environment.application == "JNBKarta") {
            this.trvMapService.onMapLoaded().pipe(
                switchMap(() => this.nvdbNavigation.searchMenuState.yearObject.valueChanges.pipe(
                    startWith(this.nvdbNavigation.searchMenuState.yearObject.value)
                ))
            ).subscribe((curYear) => {
                //let curYear = this.nvdbNavigation.searchMenuState.yearObject.value
                if (!curYear) return;
                const trvMap = this.trvMapService.trvMap!;
                const trvLayer = trvMap.trvLayer;

                //console.log("changed?", curYear, trvLayer.wmsLayerListItems.length)

                trvLayer.wmsLayerListItems.forEach(layerItem => {
                    //Get wms url from openlayers source

                    const source = layerItem.layer.getSource();
                    if (source != null && (source instanceof TileWMS || source instanceof ImageWMS)) {
                        let url: string | undefined = undefined;
                        if (source instanceof TileWMS) {
                            url = source.getUrls() ? source.getUrls()![0] : undefined;
                        } else if (source instanceof ImageWMS) {
                            url = source.getUrl();
                        }

                        if (url) {
                            if (url.includes("JNBWMS")) {
                                //strip url from JNBWMS, if ending in JNBWMS_XXX remove everything from JNBWMS
                                const baseUrl = url.split("JNBWMS")[0] + "JNBWMS" + curYear.wmsString;
                                layerItem.url = layerItem.url.split("JNBWMS")[0] + "JNBWMS" + curYear.wmsString;

                                source.setUrl(baseUrl);
                                source.refresh();


                            }

                        }

                    }


                });


            });
        }




        this.loading = false;
        this.cdref.detectChanges();

    }


    async ngAfterViewInit() {
        this.blurListener = this.renderer.listen(document, "focusout", event => {
            const target = event.target as HTMLElement;
            if (target.matches("input, textarea, .angular-editor-textarea")) {
                this.lockScroll();
            }
        });

        this.lockScroll();

        // set map extensions
        const trvMapExtension = new TrvMapExtension(
            this.trvMapService,
            this.trvMapOverlayService,
            this.nvdbNavigation,
            this.netDbService,
            this.trvMapExtensionService,
            this.globalMessagesService,
            this.trvModalService,
            this.apmService,
        );
        this.trvMapExtensionService.setTrvMapExtension(trvMapExtension);

        // go to previous view if it exists, else the default view
        let previousState = this.nvdbNavigation.previousMapState;

        const east = this.east;
        const north = this.north;
        const scale = this.scale;
        const positionProvided = east && north && scale;

        var urlContainsInformationAboutMapState = positionProvided || this.wmsLayerIds.length > 0 || this.backgroundLayerId != null;

        if (this.nvdbNavigation.VIEWMODE) {
            // DO NOTHING
        } else if (urlContainsInformationAboutMapState) {
            const trvMap = this.trvMapService.trvMap;
            const trvLayer = trvMap?.trvLayer;


            await firstValueFrom(this.trvMapService.onMapLoaded().asObservable());
            const zoom = positionProvided ? trvMap?.getZoomForScale(scale) : undefined;

            trvMap?.setMapStateWithCoord(
                positionProvided ? [east, north] : undefined,
                positionProvided ? zoom : undefined,
                this.backgroundLayerId,
                this.wmsLayerIds
            );

            //Add marker
            if (positionProvided && trvLayer) {
                trvLayer.clearAllTemporaryLayers();
                const marker = new Feature({
                    geometry: new Point([east, north]),
                });

                marker.setStyle(PinBlueUserStyle);

                trvLayer.addFeature(marker, trvLayer.tempLayer!);
            }

        } else if (previousState) {
            this.trvMapService.trvMap?.setMapStateWithCoord(
                previousState!.centerCoordinate,
                previousState!.zoom,
                previousState!.backgroundLayerId,
                previousState!.dataLayersId
            );

            this.trvMapService.trvMap!.viewHistory = previousState!.history;
            this.trvMapService.trvMap!.currentHistoryIndex = previousState!.currentHistoryIndex;
            this.trvMapService.trvMap!.hasMovedSinceLastSavepoint = previousState!.hasMovedSinceLastSavepoint;
        } else {
            const viewToGoto = this.localStorageService.savedViews.find(view => view.id === this.localStorageService.defaultViewId) ?? SWEDEN_VIEW;
            this.trvMapService.trvMap?.setMapStateWithCoord(
                viewToGoto.centerCoordinate,
                viewToGoto.zoom,
                viewToGoto.backgroundLayerId,
                viewToGoto.dataLayersId,
                2000
            );
            this.trvMapService.trvMap?.savePosition(viewToGoto.centerCoordinate, viewToGoto.zoom);
        }

    }

    // Lock the scroll on the document
    public lockScroll(): void {
        this.renderer.setStyle(document.body, "overflow", "hidden");
        this.renderer.setStyle(document.documentElement, "overflow", "hidden");

        this.renderer.setStyle(document.body, "overscroll-behavior-y", "contain");
        this.renderer.setStyle(document.documentElement, "overscroll-behavior-y", "contain");
    }

    public unlockScroll(): void {
        this.renderer.removeStyle(document.body, "overflow");
        this.renderer.removeStyle(document.documentElement, "overflow");
    }

    public displayWmsLegend(): boolean {
        return this.trvMapService.isMapLoaded() && this.getTrvMap()!.trvLayer.getVisibleWmsLayer().length > 0;
        //return this.trvMapService.mapLoaded.value && this.getTrvMap()!.trvLayer.getVisibleWmsLayer().length > 0;
    }

    public getTrvMap(): TrvMap | undefined {
        return this.trvMapService.trvMap;
    }

    public getTool(): MapToolsType {
        if (this.trvMapService.trvMap) {
            return this.trvMapService.trvMap.activeMapTool?.value;
        }
        return MapTools.None;
    }

    public changeLayer(layerItem: LayerListItem): void {
        this.trvMapService.trvMap!.trvLayer.activateBackgroundLayer(layerItem);
    }

    public mouseDown() {
        //console.log("mouse down")
    }

    public mouseUp() {
        //console.log("mouse up")
    }

    public setMapTool(mapTool: MapToolsType) {
        if (!this.trvMapService.trvMap) return;

        this.trvMapService.trvMap.changeActiveMapTool(mapTool);
    }

    ngOnDestroy() {

        if (this.blurListener) this.blurListener();
    }
}
